import { TAG, apiService } from "../ApiService";
import GameStream, { StreamStatus } from "../../model/GameStream";
import { ProgressHandler } from "../../model/ProgressHandler";
import { cancelStream } from "./cancelStream";
import { logD, logE } from "@blacknut/logging/dist";

const _poll = (
  stream: GameStream,
  firstTimeEstimation: number,
  handler: ProgressHandler,
  isCanceled: () => boolean,
) => {
  setTimeout(
    () => {
      apiService
        .get<GameStream>({
          path: `v1/streams/${stream.stream.id}`,
          requiresAuthPolicy: "user",
        })
        .subscribe({
          next: (refreshed) => {
            switch (refreshed.stream.status) {
              case StreamStatus.Error: {
                handler.onError({
                  code: refreshed.meta.errorCode || "error_launch",
                  message: refreshed.meta.message,
                });
                break;
              }
              case StreamStatus.Running: {
                handler.onProgress(100, refreshed.meta);
                handler.onCompleted(refreshed.stream, refreshed.meta);
                break;
              }
              case StreamStatus.Pending: {
                if (isCanceled()) {
                  handler.onCanceled && handler.onCanceled();
                  return;
                }
                const timeLeft = refreshed.meta.timeLeft ?? 0;
                if (timeLeft > 0 && firstTimeEstimation == 0) {
                  handler.onProgress(0, refreshed.meta);
                  _poll(refreshed, timeLeft, handler, isCanceled);
                } else if (timeLeft > 0) {
                  const percent = parseInt(
                    (
                      99 -
                      Math.min(
                        99,
                        ((refreshed.meta.timeLeft || 1) / firstTimeEstimation) * 99,
                      )
                    ).toFixed(),
                    10,
                  );
                  handler.onProgress(percent, refreshed.meta);
                  _poll(refreshed, firstTimeEstimation, handler, isCanceled);
                } else if (firstTimeEstimation > 0) {
                  handler.onProgress(99, refreshed.meta);
                  _poll(refreshed, firstTimeEstimation, handler, isCanceled);
                } else {
                  handler.onProgress(0, refreshed.meta);
                  _poll(refreshed, firstTimeEstimation, handler, isCanceled);
                }
              }
            }
          },
          error: (err: any) => {
            handler.onError({
              code: "error_launch",
              message: err.toString() || "Unknown error",
            });
          },
        });
    },
    (stream.meta.refreshIn ?? 1) * 1000,
  );
};
export const pollStream = (streamId: string, handler: ProgressHandler) => {
  let canceled = false;
  const res = {
    cancel: () => {
      canceled = true;
      cancelStream(streamId).subscribe({
        next: () => {
          logD(TAG, "%o canceled", streamId);
        },
        error: (error) => {
          logE(TAG, "Caught error on cancel: %o", error);
        },
      });
    },
  };
  apiService
    .get<GameStream>({ path: `v1/streams/${streamId}`, requiresAuthPolicy: "user" })
    .subscribe({
      next: (status) => {
        switch (status.stream.status) {
          case StreamStatus.Error: {
            handler.onError({
              code: status.meta.errorCode || "error_launch",
              message: status.meta.message,
            });
            break;
          }
          case StreamStatus.Running: {
            handler.onProgress(100, status.meta);
            handler.onCompleted(status.stream, status.meta);
            break;
          }
          case StreamStatus.Pending: {
            _poll(status, status.meta.timeLeft || 0, handler, () => canceled);
          }
        }
      },
      error: (err: any) => {
        handler.onError({
          code: "error_launch",
          message: err.toString() || "Unknown error",
        });
      },
    });
  return res;
};
